import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import {
  fetchReminders,
  deleteReminder,
  setReminderWorker,
  setReminderTodo,
  updateReminder,
  cloneReminder,
  selectAllReminders,
  selectPageInfo,
  setFilters,
  selectRemindersFilters,
  selectRemindersLoading,
  selectRemindersInitialFetchDone,
  selectPendingReminderUpdates
} from 'store/slices/remindersSlice';
import {
  updateTodo,
} from 'store/slices/todosSlice';
import Page from 'components/layout/Page/Page';
import {
  REMINDER_STATUSES,
  REMINDER_STATUSES_FILTER_OPTIONS,
  REMINDER_TYPES_OPTIONS,
  REMINDER_STATUS_FILTER_ACTIVE_OR_PAUSED,
  REMINDERS_PAGE_SIZE
} from 'constants/reminders';
import { TODO_STATUS } from 'constants/todos';
import { PrimaryButton, CloseButton, Button } from 'components/buttons';
import { AlertWarning } from 'components/layout/alerts';
import Modal from 'components/layout/modals/Modal/Modal';
import DeleteModal from 'components/layout/modals/DeleteModal/DeleteModal';
import { FilterBar, useFilterBar } from 'components/filter';
import ReminderTabs from './components/ReminderTabs/ReminderTabs';
import { CardHeader } from 'components/cards';
import { StackedList } from 'components/stackedLists';
import AssignWorkerToReminder from './components/AssignWorkerToReminder/AssignWorkerToReminder';
import AssignTodoToReminder from './components/AssignTodoToReminder/AssignTodoToReminder';
import CloneReminderModal from './components/CloneReminderModal/CloneReminderModal';

const REMINDER_TYPE_FILTER_OPTIONS = [
  { value: 'ALL', label: 'All' },
  ...REMINDER_TYPES_OPTIONS,
];

const REMINDER_LIST_FILTERS = [
  {
    key: 'search',
    type: 'search',
    label: 'Search',
    placeholder: 'Search by reminder subject',
    tier: 'primary',
  },
  {
    key: 'status',
    type: 'select',
    label: 'Status',
    options: REMINDER_STATUSES_FILTER_OPTIONS,
    tier: 'primary',
  },
  {
    key: 'type',
    type: 'select',
    label: 'Type',
    options: REMINDER_TYPE_FILTER_OPTIONS,
    tier: 'primary',
  },
];

const serializeReminderStatusFilter = (value) => {
  if (value === REMINDER_STATUS_FILTER_ACTIVE_OR_PAUSED) {
    return `${REMINDER_STATUSES.ACTIVE},${REMINDER_STATUSES.PAUSED}`;
  }

  if (value === 'ALL') {
    return undefined;
  }

  return value;
};

const REMINDER_LIST_FILTER_CONFIG = {
  route: '/reminders',
  filters: {
    status: {
      type: 'select',
      urlKey: 'status',
      default: REMINDER_STATUS_FILTER_ACTIVE_OR_PAUSED,
      options: REMINDER_STATUSES_FILTER_OPTIONS,
      serialize: serializeReminderStatusFilter,
    },
    type: {
      type: 'select',
      urlKey: 'type',
      default: 'ALL',
      options: REMINDER_TYPE_FILTER_OPTIONS,
    },
    search: {
      type: 'search',
      urlKey: 'search',
      default: '',
    },
  },
  pagination: {
    pageSize: REMINDERS_PAGE_SIZE,
  },
  fetchThunk: fetchReminders,
  setFilters,
  selectFilters: selectRemindersFilters,
  selectLoading: selectRemindersLoading,
  selectInitialFetchDone: selectRemindersInitialFetchDone,
};

function Reminders() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const reminders = useSelector(selectAllReminders);
  const pageInfo = useSelector(selectPageInfo);
  const loading = useSelector(selectRemindersLoading);
  const pendingUpdatesById = useSelector(selectPendingReminderUpdates);
  const user = useSelector((state) => state.auth.user);
  const {
    filterValues,
    isInitialLoading,
    hasActiveFilters,
    applyFilters,
    resetFilters,
    handlePageChange,
    refetchCurrentPage,
  } = useFilterBar(REMINDER_LIST_FILTER_CONFIG);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState(null);
  const [deleteReminderSubject, setDeleteReminderSubject] = useState(null);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const [reminderToAttach, setReminderToAttach] = useState(null);
  const [detachWorkerModalOpen, setDetachWorkerModalOpen] = useState(false);
  const [reminderToDetachWorker, setReminderToDetachWorker] = useState(null);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [reminderToClone, setReminderToClone] = useState(null);
  const [cloneSubmitting, setCloneSubmitting] = useState(false);
  const [detachSubmitting, setDetachSubmitting] = useState(false);
  const [assignTodoModalOpen, setAssignTodoModalOpen] = useState(false);
  const [reminderToAssignTodo, setReminderToAssignTodo] = useState(null);
  const [detachTodoModalOpen, setDetachTodoModalOpen] = useState(false);
  const [reminderToDetachTodo, setReminderToDetachTodo] = useState(null);
  const [detachTodoSubmitting, setDetachTodoSubmitting] = useState(false);
  const [todoActionModalOpen, setTodoActionModalOpen] = useState(false);
  const [todoAction, setTodoAction] = useState(null);
  const [todoActionSubmitting, setTodoActionSubmitting] = useState(false);

  const clearReminderActionParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete('action');
    params.delete('reminderId');

    const nextSearch = params.toString();
    const currentSearch = location.search.startsWith('?')
      ? location.search.slice(1)
      : location.search;

    if (nextSearch === currentSearch) {
      return;
    }

    navigate(nextSearch ? `/reminders?${nextSearch}` : '/reminders', { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    const reminderId = params.get('reminderId');

    if (!action || !reminderId) return;
    if (action !== 'todo-done' && action !== 'todo-pause') return;
    if (!reminders.length) return;

    const reminder = reminders.find((r) => r._id === reminderId);
    if (!reminder?.todo) return;

    const todo = reminder.todo;

    setTodoAction({ reminder, todo, action: action === 'todo-done' ? 'done' : 'pause' });
    setTodoActionModalOpen(true);
    clearReminderActionParams();
  }, [clearReminderActionParams, location.search, reminders]);

  const handleDeleteClick = (reminder) => {
    setReminderToDelete(reminder);
    setDeleteReminderSubject(reminder?.subject || null);
    setDeleteModalOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteModalOpen(false);
    setReminderToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (reminderToDelete) {
      try {
        await dispatch(deleteReminder(reminderToDelete._id)).unwrap();
        handleDeleteClose();
        refetchCurrentPage();
      } catch (_error) {
        // error handled by redux
      }
    }
  };

  const handleAttachClose = () => {
    setAttachModalOpen(false);
    refetchCurrentPage();
  };

  const handleAttachSuccess = () => {
    setAttachModalOpen(false);
    refetchCurrentPage();
  };

  const handleDetachWorkerClose = () => {
    setDetachWorkerModalOpen(false);
    setReminderToDetachWorker(null);
  };

  const handleConfirmDetachWorker = async () => {
    if (!reminderToDetachWorker) return;
    setDetachSubmitting(true);
    try {
      await dispatch(
        setReminderWorker({ reminderId: reminderToDetachWorker._id, workerId: null })
      ).unwrap();
      handleDetachWorkerClose();
      refetchCurrentPage();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setDetachSubmitting(false);
    }
  };

  const handleAssignTodoClose = () => {
    setAssignTodoModalOpen(false);
    refetchCurrentPage();
  };

  const handleAssignTodoSuccess = () => {
    setAssignTodoModalOpen(false);
    refetchCurrentPage();
  };

  const handleDetachTodoClose = () => {
    setDetachTodoModalOpen(false);
    setReminderToDetachTodo(null);
  };

  const handleConfirmDetachTodo = async () => {
    if (!reminderToDetachTodo) return;
    setDetachTodoSubmitting(true);
    try {
      await dispatch(
        setReminderTodo({ reminderId: reminderToDetachTodo._id, todoId: null, todoControlsReminder: false })
      ).unwrap();
      handleDetachTodoClose();
      refetchCurrentPage();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setDetachTodoSubmitting(false);
    }
  };

  const handleTodoActionClose = () => {
    setTodoActionModalOpen(false);
    setTodoAction(null);
  };

  const handleConfirmTodoAction = async () => {
    if (!todoAction) return;
    const { reminder, todo, action } = todoAction;
    const todoStatus = action === 'done' ? TODO_STATUS.DONE : TODO_STATUS.PAUSED;
    const reminderStatus = action === 'done' ? REMINDER_STATUSES.COMPLETED : REMINDER_STATUSES.PAUSED;
    setTodoActionSubmitting(true);
    try {
      await dispatch(updateTodo({ id: todo._id, data: { status: todoStatus } })).unwrap();
      if (reminder.todoControlsReminder) {
        await dispatch(updateReminder({ id: reminder._id, data: { status: reminderStatus } })).unwrap();
      }
      handleTodoActionClose();
      refetchCurrentPage();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    } finally {
      setTodoActionSubmitting(false);
    }
  };

  const handleCloneClose = () => {
    setCloneModalOpen(false);
    setReminderToClone(null);
  };

  const handleConfirmClone = async (subject) => {
    if (!reminderToClone) return;
    setCloneSubmitting(true);
    try {
      await dispatch(
        cloneReminder({
          reminderId: reminderToClone._id,
          subject,
        })
      ).unwrap();
      handleCloneClose();
      refetchCurrentPage();
    } catch (_error) {
      // error handled by redux
    } finally {
      setCloneSubmitting(false);
    }
  };

  const handleToggleStatus = async (item) => {
    const id = item._id;
    const nextStatus =
      item.status === REMINDER_STATUSES.PAUSED
        ? REMINDER_STATUSES.ACTIVE
        : REMINDER_STATUSES.PAUSED;
    try {
      await dispatch(updateReminder({ id, data: { status: nextStatus } })).unwrap();
      refetchCurrentPage();
    } catch (_error) {
      // error is captured in Redux state via rejectWithValue and displayed in the UI
    }
  };

  const handleAction = (action, item) => {
    const id = item._id;
    switch (action) {
      case 'view':
        // Assuming view navigates to edit or details page
        navigate(`edit?id=${id}`);
        break;
      case 'logs':
        navigate(`logs?reminderId=${id}`);
        break;
      case 'edit':
        navigate(`edit?id=${id}`);
        break;
      case 'delete':
        handleDeleteClick(item);
        break;
      case 'add-worker':
        setReminderToAttach(item);
        setAttachModalOpen(true);
        break;
      case 'remove-worker':
        setReminderToDetachWorker(item);
        setDetachWorkerModalOpen(true);
        break;
      case 'clone':
        setReminderToClone(item);
        setCloneModalOpen(true);
        break;
      case 'toggle-status':
        handleToggleStatus(item);
        break;
      case 'add-todo':
        setReminderToAssignTodo(item);
        setAssignTodoModalOpen(true);
        break;
      case 'remove-todo':
        setReminderToDetachTodo(item);
        setDetachTodoModalOpen(true);
        break;
      case 'add-instruction':
        break;
      default:
        break;
    }
  };

  return (
    <Page title="Reminders" headerActions={<ReminderTabs />}>
      <CardHeader
        leftAction={
          <FilterBar
            filters={REMINDER_LIST_FILTERS}
            values={filterValues}
            onChange={applyFilters}
            onReset={resetFilters}
            hasActiveFilters={hasActiveFilters}
            loading={isInitialLoading}
          />
        }
        rightAction={
          <PrimaryButton
            icon={PlusCircleIcon}
            loading={isInitialLoading}
            onClick={() => navigate('add')}
          >
            Create reminder
          </PrimaryButton>
        }
      >
        <StackedList
          items={reminders}
          settings={user?.settings}
          onAction={handleAction}
          getIsItemUpdating={(item) => Boolean(pendingUpdatesById[item._id])}
          emptyMessage="No reminders found."
          pagination={pageInfo ? {
            pageIndex: pageInfo.page - 1,
            pageSize: REMINDERS_PAGE_SIZE,
            totalItems: pageInfo.totalDocs,
            onPageChange: handlePageChange
          } : undefined}
          loading={isInitialLoading}
        />
      </CardHeader>

      <DeleteModal
        isOpen={deleteModalOpen}
        onClose={handleDeleteClose}
        title="Delete Reminder"
        message={`Are you sure you want to delete reminder "${reminderToDelete?.subject || deleteReminderSubject}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        loading={loading}
      />

      {reminderToAttach && (
        <Modal
          isOpen={attachModalOpen}
          onClose={handleAttachClose}
          className="sm:max-w-xl"
        >
          <div className="relative w-full h-auto flex flex-col bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <CloseButton onClick={handleAttachClose} className="absolute right-4 top-4" />
            <h3 className="mb-4 text-base font-semibold leading-6 text-white">Add Worker</h3>
            <AssignWorkerToReminder
              key={reminderToAttach._id}
              reminder={reminderToAttach}
              onSuccess={handleAttachSuccess}
              onCancel={handleAttachClose}
              className="flex-1"
            />
          </div>
        </Modal>
      )}

      <DeleteModal
        isOpen={detachWorkerModalOpen}
        onClose={handleDetachWorkerClose}
        title="Remove worker"
        message={`Are you sure you want to remove the assigned worker from "${reminderToDetachWorker?.subject || ''}"? The reminder itself will not be deleted.`}
        onConfirm={handleConfirmDetachWorker}
        confirmText="Remove worker"
        loading={detachSubmitting}
      />

      <CloneReminderModal
        isOpen={cloneModalOpen}
        onClose={handleCloneClose}
        onConfirm={handleConfirmClone}
        reminder={reminderToClone}
        loading={cloneSubmitting}
      />

      {reminderToAssignTodo && (
        <Modal
          isOpen={assignTodoModalOpen}
          onClose={handleAssignTodoClose}
          className="sm:max-w-xl"
        >
          <div className="relative flex h-auto w-full flex-col bg-gray-800 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <CloseButton onClick={handleAssignTodoClose} className="absolute right-4 top-4" />
            <h3 className="mb-4 text-base font-semibold leading-6 text-white">Add Todo</h3>
            <AssignTodoToReminder
              key={reminderToAssignTodo._id}
              reminder={reminderToAssignTodo}
              onSuccess={handleAssignTodoSuccess}
              onCancel={handleAssignTodoClose}
              className="flex-1"
            />
          </div>
        </Modal>
      )}

      <DeleteModal
        isOpen={detachTodoModalOpen}
        onClose={handleDetachTodoClose}
        title="Remove todo"
        message={`Are you sure you want to remove the assigned todo from "${reminderToDetachTodo?.subject || ''}"? The reminder itself will not be deleted.`}
        onConfirm={handleConfirmDetachTodo}
        confirmText="Remove todo"
        loading={detachTodoSubmitting}
      />

      {todoAction && (
        <Modal
          isOpen={todoActionModalOpen}
          onClose={handleTodoActionClose}
          className="sm:max-w-md"
        >
          <div className="relative flex flex-col bg-gray-800 px-4 pb-4 pt-5 sm:p-6">
            <CloseButton onClick={handleTodoActionClose} className="absolute right-4 top-4" />
            <h3 className="mb-4 text-base font-semibold leading-6 text-white">
              {todoAction.action === 'done' ? 'Mark Todo as Done' : 'Pause Todo'}
            </h3>

            <div className="mb-3 rounded-lg border border-gray-700 bg-gray-900 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Reminder</p>
              <p className="mt-1 font-semibold text-white">{todoAction.reminder.subject}</p>
            </div>

            <div className="mb-4 rounded-lg border border-gray-700 bg-gray-900 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Todo</p>
              <p className="mt-1 font-semibold text-white">{todoAction.todo?.name || 'The linked todo'}</p>
              <p className="mt-1 text-sm text-gray-400">
                Will be moved to{' '}
                <span className="font-medium text-white">{todoAction.action === 'done' ? 'Done' : 'Paused'}</span>
              </p>
            </div>

            {todoAction.reminder.todoControlsReminder && (
              <div className="mb-4">
                <AlertWarning>
                  {`Reminder "${todoAction.reminder.subject}" is set to follow the todo's status and will also be moved to ${todoAction.action === 'done' ? 'Completed' : 'Paused'}.`}
                </AlertWarning>
              </div>
            )}

            <div className="flex flex-col gap-y-2">
              <PrimaryButton
                onClick={handleConfirmTodoAction}
                disabled={todoActionSubmitting}
                className="w-full"
              >
                {todoActionSubmitting ? 'Confirming...' : 'Confirm'}
              </PrimaryButton>
              <Button
                onClick={handleTodoActionClose}
                disabled={todoActionSubmitting}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Page>
  );
}

export default Reminders;
